import { c as _c } from "react/compiler-runtime";
import { groupMapObj } from 'shared/collection';
import { safeParseList, zx } from 'shared/type';
import { usePortal } from 'components/portal/portal';
import { useMemo } from 'react';
import { useSbChannel } from 'utils/db/useQuerySb';
import { useCallback } from 'react';
import { z } from 'zod';
import { sb } from 'utils/db';
import { useQuery } from '@tanstack/react-query';
export type ForecastProgress = {
  completed: number;
  total?: number;
};
const zForecastProgress = z.object({
  question_id: z.string(),
  subquestion_id: zx.nullish.string,
  workspace_id: z.string(),
  code: zx.nullish.string,
  forecast_created_at: zx.nullish.string,
  forecast_created_by: zx.nullish.string,
  forecast_id: zx.nullish.string
});
type ForecastProgressRow = z.infer<typeof zForecastProgress>;
const getMyProgress = ({
  created_by,
  data
}: {
  created_by: string;
  data: ForecastProgressRow[];
}) => {
  const total = data.filter(r => r.forecast_created_by == null).length;
  const completed = data.filter(r => r.forecast_created_by == created_by).length;
  return {
    completed,
    total
  } as ForecastProgress;
};
export const useMyForecastProgress = ({
  question_id,
  pushDownQuestionFilter
}: {
  question_id: string;
  pushDownQuestionFilter?: boolean;
}) => {
  const {
    profile
  } = usePortal();
  const {
    data: data_0
  } = useProgressQuery({
    filter: {
      created_by: profile.id,
      question_id
    },
    select: data => data.filter(r => r.question_id == question_id && (r.forecast_created_by == profile.id || r.forecast_created_by == null)),
    pushDownQuestionFilter
  });
  return useMemo(() => data_0 ? getMyProgress({
    created_by: profile.id,
    data: data_0
  }) : undefined, [data_0]);
};
const useProgressQuery = t0 => {
  const $ = _c(25);
  const {
    filter,
    select,
    pushDownQuestionFilter
  } = t0;
  const {
    workspace_id,
    workspace: t1,
    profile
  } = usePortal();
  const {
    advancedUI
  } = t1;
  let t2;
  if ($[0] !== filter.question_id || $[1] !== pushDownQuestionFilter) {
    t2 = pushDownQuestionFilter ? {
      question_id: filter.question_id
    } : {};
    $[0] = filter.question_id;
    $[1] = pushDownQuestionFilter;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2 || $[4] !== workspace_id) {
    t3 = {
      workspace_id,
      ...t2
    };
    $[3] = t2;
    $[4] = workspace_id;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const sbFilter = t3;
  let t4;
  if ($[6] !== sbFilter) {
    t4 = ["forecast", "useProgressQuery", sbFilter];
    $[6] = sbFilter;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== filter.created_by || $[9] !== pushDownQuestionFilter || $[10] !== sbFilter) {
    t5 = async () => {
      let query = sb.from("forecast_progress").select("*").match(sbFilter);
      if (filter.created_by != null) {
        query = query.or(`forecast_created_by.eq.${filter.created_by},forecast_created_by.is.null`);
      }
      if (!pushDownQuestionFilter) {
        query = query.eq("question_status", "forecasting");
      }
      const {
        data: forecasts,
        error
      } = await query;
      if (error != null) {
        throw Error(error?.message);
      }
      return safeParseList(zForecastProgress, forecasts);
    };
    $[8] = filter.created_by;
    $[9] = pushDownQuestionFilter;
    $[10] = sbFilter;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== advancedUI || $[13] !== select || $[14] !== t4 || $[15] !== t5) {
    t6 = {
      queryKey: t4,
      queryFn: t5,
      enabled: advancedUI,
      select
    };
    $[12] = advancedUI;
    $[13] = select;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const query_0 = useQuery(t6);
  let t7;
  if ($[17] !== filter.question_id) {
    t7 = ["question_id", filter.question_id];
    $[17] = filter.question_id;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== profile || $[20] !== query_0) {
    t8 = d => {
      if (d.eventType == "UPDATE") {
        return;
      }
      const isMe = d.eventType == "INSERT" && d.new.created_by == profile.id || d.eventType == "DELETE" && d.old.created_by == profile.id;
      if (!isMe) {
        return;
      }
      console.log("refetching forecast progress because forecast changed");
      query_0.refetch();
    };
    $[19] = profile;
    $[20] = query_0;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t7 || $[23] !== t8) {
    t9 = {
      table: "forecast",
      filter: t7,
      onChange: t8
    };
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  useSbChannel(t9);
  return query_0;
};
export const useForecastProgress = t0 => {
  const $ = _c(9);
  const {
    filter
  } = t0;
  let t1;
  if ($[0] !== filter) {
    t1 = {
      filter
    };
    $[0] = filter;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data
  } = useProgressQuery(t1);
  let t2;
  bb0: {
    if (data == null) {
      t2 = undefined;
      break bb0;
    }
    let t3;
    if ($[2] !== data) {
      t3 = groupMapObj(data, _temp, _temp5);
      $[2] = data;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    t2 = t3;
  }
  const progressByQuestionForecaster = t2;
  let t3;
  if ($[4] !== progressByQuestionForecaster) {
    t3 = t4 => {
      const {
        profile_id,
        question_id
      } = t4;
      if (progressByQuestionForecaster == null) {
        return;
      }
      const byForecaster = progressByQuestionForecaster[question_id];
      if (byForecaster == null) {
        return;
      }
      return byForecaster[profile_id] ?? byForecaster._;
    };
    $[4] = progressByQuestionForecaster;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const getForecasterProgress = t3;
  let t4;
  if ($[6] !== data || $[7] !== getForecasterProgress) {
    t4 = {
      getForecasterProgress,
      data
    };
    $[6] = data;
    $[7] = getForecasterProgress;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
};
export type UseForecastProgress = ReturnType<typeof useForecastProgress>;
function _temp(r) {
  return r.question_id;
}
function _temp2(r_0) {
  return r_0.forecast_created_by == null;
}
function _temp3(r_1) {
  return r_1.forecast_created_by != null;
}
function _temp4(r_2) {
  return r_2.forecast_created_by;
}
function _temp5(rs) {
  const vanilla = rs.filter(_temp2);
  const total = vanilla.length;
  return {
    ...groupMapObj(rs.filter(_temp3), _temp4, fs => ({
      completed: fs.length,
      total
    }) satisfies ForecastProgress),
    _: {
      completed: 0,
      total
    }
  } as Record<string, ForecastProgress>;
}